import React from 'react';
import { ExperienceLevel } from '../../types/athlete';
import { Award, TrendingUp, Zap, Brain } from 'lucide-react';

interface StepExperienceProps {
  experienceLevel: ExperienceLevel | null;
  onUpdate: (level: ExperienceLevel) => void;
}

const experienceOptions: {
  level: ExperienceLevel;
  label: string;
  description: string;
  icon: React.ReactNode;
  details: string[];
}[] = [
  {
    level: 'beginner',
    label: 'Beginner',
    description: 'New to triathlon or returning after a long break',
    icon: <Brain className="w-6 h-6" />,
    details: [
      'Less than 1 year of consistent training',
      'May have completed 0-3 triathlons',
      'Building foundational fitness',
      'Focus on consistency over intensity',
    ],
  },
  {
    level: 'intermediate',
    label: 'Intermediate',
    description: 'Has completed several races, training consistently',
    icon: <TrendingUp className="w-6 h-6" />,
    details: [
      '1-3 years of consistent training',
      'Completed 3+ triathlons',
      'Good base fitness established',
      'Ready to focus on performance',
    ],
  },
  {
    level: 'advanced',
    label: 'Advanced',
    description: 'Experienced athlete looking to optimize performance',
    icon: <Award className="w-6 h-6" />,
    details: [
      '3+ years of structured training',
      'Multiple race finishes at various distances',
      'Strong base and race experience',
      'Seeking marginal gains',
    ],
  },
];

const StepExperience: React.FC<StepExperienceProps> = ({ experienceLevel, onUpdate }) => {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold mb-2">Your Experience Level</h2>
        <p className="text-muted">This helps us customize the training intensity and progression.</p>
      </div>

      <div className="space-y-4">
        {experienceOptions.map((option) => {
          const isSelected = experienceLevel === option.level;
          return (
            <button
              key={option.level}
              onClick={() => onUpdate(option.level)}
              className={`card-base p-6 text-left w-full transition-all ${
                isSelected
                  ? 'ring-2 ring-[#a855f7] bg-[rgba(168,85,247,0.1)]'
                  : 'hover:bg-[#1e293b]'
              }`}
            >
              <div className="flex items-start space-x-4">
                <div
                  className={`p-3 rounded-lg ${
                    isSelected ? 'bg-[#a855f7] text-white' : 'bg-[#141921] text-[#a855f7]'
                  }`}
                >
                  {option.icon}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-lg">{option.label}</p>
                    {isSelected && (
                      <span className="px-3 py-1 bg-[#a855f7] text-white text-xs rounded-full font-medium">
                        Selected
                      </span>
                    )}
                  </div>
                  <p className="text-muted mt-1">{option.description}</p>
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2">
                    {option.details.map((detail, idx) => (
                      <div key={idx} className="flex items-center space-x-2 text-sm text-[#94a3b8]">
                        <Zap size={14} className="text-[#a855f7]" />
                        <span>{detail}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default StepExperience;
